import {useAufgabenHandling} from "../../hooks/aufgaben/useAufgabenHandling";
import {Alert, Box, capitalize, FormControlLabel, Switch, Typography} from "@mui/material";
import {aufgabenStyles} from "../../styles/aufgaben/aufgaben.styles";
import {Field, useFormikContext} from "formik";
import {TextField} from 'formik-mui';
import GenericSelect from "../data/GenericSelect";
import {useListPhoneNumbers} from "../../client/queries";
import {useSchedulers} from "../../client/queries";
import {useOnboardingHandling} from "../../hooks/onboarding/useOnboardingHandling";
import {Mandant, Scheduler} from "../../api";
import {useMandantenHandling} from "../../hooks/mandanten/useMandantenHandling";
import {useSchedulersHandling} from "../../hooks/schedulers/useSchedulersHandling";
import {ChangeEvent, ReactNode, useMemo} from "react";
import {DEFAULT_LANGUAGE} from "../../config/language";
import type {AufgabeFormValues} from "../../types/aufgabenTypes";
import {useUserMandant} from "../../hooks/useUserMandant";
import FieldsInboundConfig from "../shared/FieldsInboundConfig";
import {Link as RouterLink} from "react-router-dom";

interface FieldsContentAufgabeProps {
    canEditFields: boolean;
    mandant?: Mandant;
    aufgabeId?: string;
    toolsSection?: ReactNode;
    showPromptFields?: boolean;
}


export default function FieldsContentAufgabe({mandant, aufgabeId, canEditFields, toolsSection, showPromptFields = true}: FieldsContentAufgabeProps) {
    const {translation,translationLanguagesCodes} = useAufgabenHandling();
    const {loadMandanten} = useMandantenHandling();
    const {setFieldValue, values} = useFormikContext<AufgabeFormValues>();
    const {isSystemUser} = useUserMandant();
    const {removeNumberPrefix, getCountryCode} = useOnboardingHandling();
    const inboundEnabled = (values.inboundMode ?? 'NIE') !== 'NIE';
    const phoneNumberListe = useListPhoneNumbers({enabled: isSystemUser || inboundEnabled});

    const phoneNumberOptions = useMemo(
        () => (phoneNumberListe?.items ?? []).map((pn) => ({
            id: removeNumberPrefix(pn.number),
            name: pn.number,
            descriptor: pn.descriptor ?? '',
        })),
        [phoneNumberListe?.items, removeNumberPrefix]
    );

    const selectedPhoneNumber = useMemo(() => {
        const value = values.telefonnummer;
        if (!value) return undefined;
        return phoneNumberOptions.find((option) => option.id === value)
            ?? {id: value, name: `${getCountryCode()}${value}`, descriptor: ''};
    }, [values.telefonnummer, phoneNumberOptions, getCountryCode]);

    const linkedPhoneNumber = useMemo(() => {
        const value = values.telefonnummer;
        if (!value) return undefined;
        return (phoneNumberListe?.items ?? []).find(
            (pn) => removeNumberPrefix(pn.number) === value
        );
    }, [values.telefonnummer, phoneNumberListe?.items, removeNumberPrefix]);

    const {scopeLabel} = useSchedulersHandling();
    const schedulerListe = useSchedulers({
        selectableForAufgabe: aufgabeId,
        // Aufgabe doesn't exist yet (create flow): fall back to selecting by Mandant so GLOBAL
        // schedulers (and the chosen Mandant's own) are shown even before the Aufgabe is saved.
        selectableForMandant: aufgabeId ? undefined : (values.mandantId ?? ''),
        limit: 200,
        sort: "name",
    });

    const schedulerOptions = useMemo(() => schedulerListe?.items ?? [], [schedulerListe?.items]);

    const selectedScheduler = useMemo(() => {
        const schedulerId = values.schedulerId;
        if (!schedulerId) return undefined;
        return schedulerOptions.find(option => option.id === schedulerId);
    }, [values.schedulerId, schedulerOptions]);

    const statusOptions = useMemo(() => [
        { id: 'PAUSIERT', name: 'Pausiert' },
        { id: 'LIVE', name: 'Live' },
        { id: 'ABGESCHLOSSEN', name: 'Abgeschlossen' }
    ], []);

    const selectedStatus = useMemo(() => {
        const statusValue = (values as any).status;
        if (!statusValue) {
            return { id: 'PAUSIERT', name: 'Pausiert' };
        }
        const foundOption = statusOptions.find(option => option.id === statusValue);
        if (foundOption) {
            return foundOption;
        }
        return { id: statusValue, name: statusValue };
    }, [values, statusOptions]);

    const inboundModeOptions = useMemo(() => [
        { id: 'NIE', name: capitalize(translation('task-attributes-inbound-mode-nie')) },
        { id: 'NUR_BEI_TREFFER', name: capitalize(translation('task-attributes-inbound-mode-nur-bei-treffer')) },
        { id: 'IMMER', name: capitalize(translation('task-attributes-inbound-mode-immer')) },
    ], [translation]);

    const selectedInboundMode = useMemo(() => {
        const value = values.inboundMode ?? 'NIE';
        return inboundModeOptions.find(option => option.id === value) ?? inboundModeOptions[0];
    }, [values.inboundMode, inboundModeOptions]);


    return (
        <Box id={'fields-content-aufgabe-box'}
             display={"grid"}
             sx={aufgabenStyles.fieldsContentAufgabe.box}
        >
            <Field
                component={TextField}
                label={capitalize(translation('task-attributes-title'))}
                name='titel'
                required
                disabled={!canEditFields}
            />
            <Field
                component={TextField}
                label={capitalize(translation('task-attributes-description'))}
                name='beschreibung'
                disabled={!canEditFields}
            />
            <Field
                component={GenericSelect}
                name={"mandantId"}
                label={capitalize(translation('task-attributes-client'))}
                loadOptions={loadMandanten}
                getOptionLabel={(mandant: Mandant) => mandant.name}
                getSecondaryLabel={(user: Mandant) => user.id}
                setSelected={(value: string) => setFieldValue("mandantId", value)}
                initialSelected={mandant}
                required
                sort={"name"}
                disabled={!canEditFields}
            />
            {isSystemUser && (
                <Field
                    component={GenericSelect}
                    name={"telefonnummer"}
                    label={capitalize(translation('task-attributes-phone-number'))}
                    options={phoneNumberOptions}
                    getOptionLabel={(option: { id: string, name: string, descriptor?: string }) =>
                        option.descriptor ? `${option.name} — ${option.descriptor}` : option.name}
                    setSelected={(value: string) => setFieldValue("telefonnummer", value)}
                    initialSelected={selectedPhoneNumber}
                    required={false}
                    disabled={!canEditFields}
                />
            )}
            <FieldsInboundConfig canEdit={canEditFields}/>
            <Field
                key={selectedScheduler?.id ?? 'default'}
                component={GenericSelect}
                name={"schedulerId"}
                label={capitalize(translation('task-attributes-scheduler'))}
                options={schedulerOptions}
                getOptionLabel={(option: Scheduler) => option.name}
                getSecondaryLabel={(option: Scheduler) => scopeLabel(option.scope)}
                setSelected={(value: string) => setFieldValue("schedulerId", value)}
                initialSelected={selectedScheduler}
                required
                disabled={!canEditFields}
            />
            <Field
                component={GenericSelect}
                name={"status"}
                label={capitalize(translation('task-attributes-task-status'))}
                options={statusOptions}
                getOptionLabel={(option: { id: string, name: string }) => option.name}
                getSecondaryLabel={(option: { id: string, name: string }) => option.id}
                setSelected={(value: string) => setFieldValue("status", value)}
                initialSelected={selectedStatus}
                required
                disabled={!canEditFields}
            />
            <Field
                component={TextField}
                label={capitalize(translation('task-attributes-auftragssystem-auftrag-id'))}
                name='auftragssystemAuftragId'
                required
                disabled={!canEditFields}
            />
            <Field
                component={TextField}
                label={capitalize(translation('task-attributes-max-contact-retries'))}
                name='anzahlAnrufversucheMax'
                type='number'
                inputProps={{ min: 1, step: 1 }}
                disabled={!canEditFields}
            />
            <Field
                component={GenericSelect}
                name={"inboundMode"}
                label={capitalize(translation('task-attributes-inbound-mode'))}
                options={inboundModeOptions}
                getOptionLabel={(option: { id: string, name: string }) => option.name}
                setSelected={(value: string) => setFieldValue('inboundMode', value)}
                initialSelected={selectedInboundMode}
                required
                disabled={!canEditFields}
            />
            {inboundEnabled && linkedPhoneNumber && (
                <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>
                    {translation('task-inbound-linked-phone-number')}{' '}
                    <RouterLink to={`/phonenumbers/${linkedPhoneNumber.id}`} style={{color: 'inherit', fontWeight: 600}}>
                        {linkedPhoneNumber.number}{linkedPhoneNumber.descriptor ? ` — ${linkedPhoneNumber.descriptor}` : ''}
                    </RouterLink>
                    {linkedPhoneNumber.activeIcpFallbackPromptId
                        ? ` · ${translation('task-inbound-phone-has-fallback')}`
                        : ` · ${translation('task-inbound-phone-no-fallback')}`}
                </Alert>
            )}
            {inboundEnabled && (
                <>
                    <Field
                        component={TextField}
                        label={capitalize(translation('task-attributes-inbound-config-duration-in-hours'))}
                        name='inboundConfigDurationInHours'
                        type='number'
                        disabled={!canEditFields}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.updateInboundConfigTimeOnUpdate ?? false}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const checked = e.target.checked;
                                    setFieldValue('updateInboundConfigTimeOnUpdate', checked);
                                }}
                                disabled={!canEditFields}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" >
                                {capitalize(translation('task-attributes-update-inbound-config-time-on-update'))}
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.useInboundPrompt ?? false}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const checked = e.target.checked;
                                    setFieldValue('useInboundPrompt', checked);
                                }}
                                disabled={!canEditFields}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" >
                                {capitalize(translation('task-attributes-use-inbound-prompt'))}
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.useSeparateToolsForInbound ?? false}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const checked = e.target.checked;
                                    setFieldValue('useSeparateToolsForInbound', checked);
                                }}
                                disabled={!canEditFields}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" >
                                {capitalize(translation('task-attributes-use-separate-tools-for-inbound'))}
                            </Typography>
                        }
                    />
                </>
            )}
            {toolsSection && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                    {toolsSection}
                </Box>
            )}
            {showPromptFields && (
                <Field
                    sx={values.sprache !== DEFAULT_LANGUAGE && values.sprache.length === 5 ? aufgabenStyles.fieldsContentAufgabe.prompt2langFieldFirst: aufgabenStyles.fieldsContentAufgabe.promptField }
                    component={TextField}
                    label={capitalize(translation('task-attributes-prompt'))}
                    name='prompt'
                    required
                    disabled={!canEditFields}
                    multiline
                    rows={20}
                />
            )}
            {showPromptFields && values.sprache !== DEFAULT_LANGUAGE && values.sprache.length === 5 && (<Field
                sx={aufgabenStyles.fieldsContentAufgabe.prompt2langField}
                component={TextField}
                label={`${capitalize(translation('task-attributes-prompt'))} (${translationLanguagesCodes(values.sprache)})`}
                name='foreignPrompt'
                required
                disabled={!canEditFields}
                multiline
                rows={20}
            />)}
        </Box>
    );
}
